// Settings → Notifications — per-type in-app notification preferences.
//
// Categories render as master toggles that expand to per-type switches.
// The server stores only *muted* keys: a category key mutes the whole group
// (including future types added to it), an exact type key mutes just that
// one. Muted notifications are never created — no row, no push, no badge.
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Bell, ChevronDown, Check } from "lucide-react";
import { API_URL } from "../../api/api";
import { T } from "../../components/em/tokens";
import { SettingsCard, Toggle } from "./settingsUi";

const auth = () => {
  const token = localStorage.getItem("token");
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
};

const NotificationSettings = () => {
  const [categories, setCategories] = useState([]);
  const [muted, setMuted] = useState(new Set());
  const [open, setOpen] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savedTick, setSavedTick] = useState(false);
  const saveSeq = useRef(0);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(
          `${API_URL}/api/notifications/preferences`,
          auth(),
        );
        setCategories(res.data?.categories || []);
        setMuted(new Set(res.data?.muted || []));
      } catch (e) {
        setError(
          e?.response?.data?.error || "Could not load your preferences.",
        );
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Persist a new muted set (optimistic — revert on failure).
  const save = async (nextSet) => {
    const prev = muted;
    setMuted(nextSet);
    const seq = ++saveSeq.current;
    try {
      await axios.put(
        `${API_URL}/api/notifications/preferences`,
        { muted: [...nextSet] },
        auth(),
      );
      if (seq === saveSeq.current) {
        setSavedTick(true);
        setTimeout(() => setSavedTick(false), 1400);
      }
    } catch (e) {
      if (seq === saveSeq.current) {
        setMuted(prev);
        setError(e?.response?.data?.error || "Could not save — try again.");
        setTimeout(() => setError(null), 3000);
      }
    }
  };

  // Category state: 'on' (nothing muted) / 'off' (all muted) / 'mixed'.
  const catState = useMemo(() => {
    const map = {};
    for (const cat of categories) {
      if (muted.has(cat.key)) {
        map[cat.key] = "off";
        continue;
      }
      const mutedTypes = cat.types.filter((t) => muted.has(t.key)).length;
      map[cat.key] =
        mutedTypes === 0 ? "on" : mutedTypes === cat.types.length ? "off" : "mixed";
    }
    return map;
  }, [categories, muted]);

  const toggleCategory = (cat) => {
    const next = new Set(muted);
    // Always clear any per-type keys for this category first.
    cat.types.forEach((t) => next.delete(t.key));
    if (catState[cat.key] === "on") {
      next.add(cat.key); // ON → mute the whole group
    } else {
      next.delete(cat.key); // OFF / MIXED → everything back on
    }
    save(next);
  };

  const toggleType = (cat, type) => {
    const next = new Set(muted);
    // If the whole category was muted, expand it into per-type keys so a
    // single type can be re-enabled.
    if (next.has(cat.key)) {
      next.delete(cat.key);
      cat.types.forEach((t) => next.add(t.key));
    }
    if (next.has(type.key)) next.delete(type.key);
    else next.add(type.key);
    // Normalise: if every type ended up muted, collapse to the category key
    // so future types in this group are covered too.
    if (cat.types.every((t) => next.has(t.key))) {
      cat.types.forEach((t) => next.delete(t.key));
      next.add(cat.key);
    }
    save(next);
  };

  if (loading) {
    return (
      <SettingsCard icon={Bell} title="Notifications" subtitle="Loading your preferences…">
        <div style={{ color: T.fg7, font: `400 13px ${T.font}` }}>Loading…</div>
      </SettingsCard>
    );
  }

  return (
    <SettingsCard
      icon={Bell}
      title="Notifications"
      subtitle="Choose which in-app notifications you receive. Muted types are never delivered."
      actions={
        savedTick ? (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              color: "#15803d",
              font: `600 12px ${T.font}`,
            }}
          >
            <Check size={14} /> Saved
          </span>
        ) : null
      }
    >
      {error && (
        <div
          style={{
            background: "rgba(220,38,38,0.08)",
            border: "1px solid rgba(220,38,38,0.25)",
            color: "#b91c1c",
            font: `500 13px ${T.font}`,
            borderRadius: 10,
            padding: "10px 14px",
            marginBottom: 14,
          }}
        >
          {error}
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {categories.map((cat) => {
          const state = catState[cat.key];
          const isOpen = !!open[cat.key];
          return (
            <div
              key={cat.key}
              style={{
                border: `1px solid ${T.border}`,
                borderRadius: 14,
                overflow: "hidden",
                background: "var(--em-bg-card)",
              }}
            >
              {/* Category header row */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "14px 16px",
                }}
              >
                <button
                  type="button"
                  onClick={() => setOpen((o) => ({ ...o, [cat.key]: !isOpen }))}
                  aria-expanded={isOpen}
                  aria-label={`${isOpen ? "Collapse" : "Expand"} ${cat.label}`}
                  style={{
                    border: 0,
                    background: "none",
                    cursor: "pointer",
                    color: T.fg7,
                    display: "inline-flex",
                    padding: 2,
                    transform: isOpen ? "rotate(180deg)" : "none",
                    transition: "transform 160ms ease",
                  }}
                >
                  <ChevronDown size={16} />
                </button>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ font: `600 14px ${T.font}`, color: T.fg1 }}>
                    {cat.label}
                    {state === "mixed" && (
                      <span
                        style={{
                          marginLeft: 8,
                          font: `600 10.5px ${T.font}`,
                          letterSpacing: "0.04em",
                          textTransform: "uppercase",
                          color: "#b45309",
                          background: "rgba(245,158,11,0.14)",
                          borderRadius: 999,
                          padding: "3px 8px",
                          verticalAlign: "middle",
                        }}
                      >
                        Custom
                      </span>
                    )}
                  </div>
                  <div style={{ font: `400 12.5px ${T.font}`, color: T.fg7, marginTop: 2 }}>
                    {cat.description}
                  </div>
                </div>
                <Toggle
                  checked={state !== "off"}
                  onChange={() => toggleCategory(cat)}
                />
              </div>

              {/* Per-type switches */}
              {isOpen && (
                <div
                  style={{
                    borderTop: `1px solid ${T.border}`,
                    background: "rgba(0,0,0,0.015)",
                    padding: "6px 16px 10px 44px",
                  }}
                >
                  {cat.types.map((type) => {
                    const typeOn = !muted.has(cat.key) && !muted.has(type.key);
                    return (
                      <div
                        key={type.key}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 12,
                          padding: "9px 0",
                        }}
                      >
                        <span style={{ font: `400 13px ${T.font}`, color: T.fg1 }}>
                          {type.label}
                        </span>
                        <Toggle checked={typeOn} onChange={() => toggleType(cat, type)} />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <p style={{ font: `400 12px ${T.font}`, color: T.fg7, marginTop: 16, marginBottom: 0 }}>
        Changes apply immediately. Muting a whole category also covers new
        notification types added to it later.
      </p>
    </SettingsCard>
  );
};

export default NotificationSettings;
